import React, { useContext } from "react";
import { useWindowDimensions } from "react-native";
import { flatten, uniqBy } from "lodash";
import { isAvailable } from "../../utils/logic";
import { QuestionType } from "../../utils/contentful";
import { QuizType } from "../../utils/quiz";
import { FurnitureContext } from "../../utils/furniture";
import LinearColorGraph from "../LinearColorGraph";
import { template } from "../../utils/locale";
import { PriorityOption } from "../PrioritiesResult";
import { InlineRichText } from "../../components/RichText";
import { externalStateReducer } from "../../utils/logic";
import { useExternalState } from "../../utils/me";
import {
    Container,
    Item,
    Hero,
    HeroImage,
    HeroOverlay,
    Copy,
    Title,
    Caption,
    AnswerCount,
    Prefix,
    Summary,
} from "./styles";

type QuizQueueProps = {
    queue: any;
    isComplete?: boolean;
    isDisabled?: boolean;
    quizStates?: any;
    logicStates?: any;
    skipId?: string;
    onPressItem?: (key: string) => void;
};

const QuizQueue = ({
    queue,
    isComplete,
    isDisabled,
    quizStates,
    logicStates,
    skipId = "",
    onPressItem,
}: QuizQueueProps) => {
    const { width } = useWindowDimensions();
    const windowWidth = Math.floor(width);
    const externalState = useExternalState();
    const {
        strings: {
            quizQueueLabelYouAnswered,
            quizQueueLabelYouReviewed,
            quizQueueLabelYouScored,
            quizReviewLabelNumberOfTotal,
            quizQueueLabeledAnswered,
            quizReviewLabelAllFactorsEqual,
            quizReviewLabelNumberReviewed,
        },
    } = useContext(FurnitureContext);
    const getNumberAnswered: (values: {
        answers: number;
        total: number;
    }) => string = template(quizReviewLabelNumberOfTotal);
    const getNumberReviewed: (value: { total: number }) => string = template(
        quizReviewLabelNumberReviewed,
    );
    return (
        <Container isDisabled={isDisabled}>
            {queue
                .filter(quiz => quiz._id !== skipId)
                .map(quiz => {
                    // answeredQuestions
                    let questionCount, tally, externalQuizState, logicState;

                    const externalLogicStates = externalState?.logicStates;
                    const reducedLogicState =
                        externalStateReducer(externalLogicStates);

                    const imageUrl = `https:${quiz.photo.url}?w=${windowWidth}`;

                    const { title, titleRich } = quiz;

                    const theTitle = titleRich ? (
                        <InlineRichText doc={titleRich} />
                    ) : (
                        title
                    );

                    const availableQuestionCount = quiz?.questions
                        .filter(question => {
                            return isAvailable(question, {
                                external: reducedLogicState,
                                internal: null,
                            });
                        })
                        .reduce((acc, _question) => {
                            if (
                                _question.type === QuestionType.Interstitial &&
                                quiz.quizType != QuizType.Informational
                            ) {
                                return acc;
                            }

                            return acc + 1;
                        }, 0);

                    if (isComplete) {
                        externalQuizState = quizStates.find(
                            quizState => quizState.quizId === quiz._id,
                        );

                        questionCount =
                            externalQuizState?.questionDisplayCount || 0;

                        // answeredQuestions = questionCount;

                        logicState = logicStates.find(
                            logicState => logicState.quizId === quiz._id,
                        );

                        if (logicState?.local) {
                            const tallyKey = quiz.result.tallyKey || "DEFAULT";
                            tally = logicState.local[tallyKey] || 0;
                        }
                    }

                    const renderTally = (
                        isComplete,
                        questionCount,
                        quiz,
                        tally,
                    ) => {
                        if (
                            !isComplete ||
                            [
                                QuizType.LinearRating,
                                QuizType.Priorities,
                            ].includes(quiz.type)
                        )
                            return null;

                        let prefix,
                            count = questionCount;

                        switch (quiz.type) {
                            case QuizType.Default:
                            case QuizType.Priorities:
                                tally = questionCount;
                                prefix =
                                    quiz?.result?.prefix ||
                                    quizQueueLabelYouAnswered;
                                break;

                            case QuizType.Informational:
                                tally = quiz?.questions?.length || 0;
                                prefix =
                                    quiz?.result?.prefix ||
                                    quizQueueLabelYouReviewed;
                                // count = quiz?.questions?.length || questionCount;
                                break;

                            case QuizType.SimpleChecklist:
                                prefix =
                                    quiz?.result?.prefix ||
                                    quizQueueLabelYouAnswered;
                                break;

                            case QuizType.KnowledgeTest:
                                prefix =
                                    quiz?.result?.prefix ||
                                    quizQueueLabelYouScored;
                                break;
                        }

                        if (quiz.type === QuizType.Informational) {
                            return (
                                <>
                                    <Caption type="body2">{prefix}</Caption>
                                    <AnswerCount type="mono2">
                                        {getNumberAnswered({
                                            answers: tally,
                                            total: tally,
                                        })}
                                    </AnswerCount>
                                </>
                            );
                        }

                        return (
                            <>
                                <Caption type="body2">{prefix}</Caption>
                                <AnswerCount type="mono2">
                                    {getNumberAnswered({
                                        answers: tally,
                                        total: count,
                                    })}
                                </AnswerCount>
                            </>
                        );
                    };

                    const renderSummary = (isComplete, quiz, tally) => {
                        if (!isComplete) return null;
                        let summary;

                        const result = {
                            ...quiz.result,
                            displayCaratScore: false,
                        };

                        const levelMatch = result.levels?.reduce(
                            (acc, level) =>
                                tally >= level.tallyThreshold ? level : acc,
                            result.levels[0],
                        );

                        if (levelMatch) {
                            summary = levelMatch.titleRich ? (
                                <InlineRichText doc={levelMatch.titleRich} />
                            ) : (
                                levelMatch.title
                            );
                        }
                        return (
                            <Summary type="mono1" allowFontScaling={false}>
                                {summary}
                            </Summary>
                        );
                    };

                    const renderLinearGraph = (isComplete, quiz, tally) => {
                        if (!isComplete || quiz.type !== QuizType.LinearRating)
                            return null;

                        let summary;

                        const result = {
                            ...quiz.result,
                            displayCaratScore: false,
                        };

                        const levelMatch = result.levels?.reduce(
                            (acc, level) =>
                                tally >= level.tallyThreshold ? level : acc,
                            result.levels[0],
                        );

                        if (levelMatch) {
                            summary = levelMatch.titleRich ? (
                                <InlineRichText doc={levelMatch.titleRich} />
                            ) : (
                                levelMatch.title
                            );
                        }

                        return (
                            <>
                                <Prefix
                                    type="caption1"
                                    allowFontScaling={false}>
                                    {result.prefix}
                                </Prefix>
                                <Summary type="mono1" allowFontScaling={false}>
                                    {summary}
                                </Summary>
                                <LinearColorGraph
                                    inline
                                    result={result}
                                    tally={tally}
                                    levelMatchIndex={result.levels.indexOf(
                                        levelMatch,
                                    )}
                                />
                            </>
                        );
                    };

                    const renderPriorities = (isComplete, quiz) => {
                        // this logic is borrowed from Priorities Result component
                        // but here we don't need score calculations
                        // and quiz passed down instead of taken from context
                        if (!isComplete || quiz.type !== QuizType.Priorities)
                            return null;

                        let prefix =
                            quiz?.result?.prefix || quizQueueLabelYouReviewed;

                        const availableQuestions =
                            externalQuizState?.availableQuestions;
                        const priorityOptions = availableQuestions
                            ?.filter(q => q.type === QuestionType.Priority)
                            .map(q => q["options"]);
                        const priorities = uniqBy(
                            flatten(priorityOptions),
                            "_id",
                        );
                        // TODO: Cast priority to a type to prevent TypeScript compile errors
                        const hasNoWinner = priorities.every(
                            // @ts-ignore
                            (p: PriorityOption) =>
                                p.score === priorities[0].score,
                        );

                        priorities.sort((a: any, b: any) => {
                            return b.score - a.score;
                        });

                        let topPriority = hasNoWinner
                            ? null
                            : priorities.shift();

                        if (topPriority) {
                            topPriority = topPriority["bodyRich"] ? (
                                <InlineRichText doc={topPriority["bodyRich"]} />
                            ) : (
                                topPriority["body"]
                            );
                        }
                        if (!topPriority) {
                            topPriority = quizReviewLabelAllFactorsEqual;
                        }

                        return (
                            <>
                                <Caption type="body2">{prefix}</Caption>
                                <AnswerCount type="mono2">
                                    {topPriority}
                                </AnswerCount>
                            </>
                        );
                    };

                    const renderCaption = (
                        availableQuestionCount,
                        quizType,
                    ) => {
                        if (quizType === QuizType.Informational) {
                            return (
                                <Caption type="body2">
                                    {getNumberReviewed({ total: 0 })}
                                </Caption>
                            );
                        } else {
                            return (
                                <Caption type="body2">{`0/${
                                    availableQuestionCount || 0
                                } ${quizQueueLabeledAnswered}`}</Caption>
                            );
                        }
                    };

                    return (
                        <Item
                            key={quiz._id}
                            disabled={isDisabled}
                            onPress={() => {
                                onPressItem?.(quiz._id);
                            }}>
                            <Hero>
                                <HeroImage source={{ uri: imageUrl }} />
                                {isComplete && <HeroOverlay></HeroOverlay>}
                            </Hero>
                            <Copy>
                                <Title type={isComplete ? "body1" : "h10"}>
                                    {theTitle}
                                </Title>
                                {!isComplete &&
                                    renderCaption(
                                        availableQuestionCount,
                                        quiz.type,
                                    )}
                                {/* Check to see if 
								1- display Tally is on and if the contentful title does contain "toxic"or
								2- if the contentful title does not contain toxic */}
                                {quiz?.result?.displayResultsTally
                                    ? renderTally(
                                          isComplete,
                                          questionCount,
                                          quiz,
                                          tally,
                                      )
                                    : renderSummary(isComplete, quiz, tally)}
                                {renderLinearGraph(isComplete, quiz, tally)}
                                {renderPriorities(isComplete, quiz)}
                            </Copy>
                        </Item>
                    );
                })}
        </Container>
    );
};
export default QuizQueue;
